import { describeBenefit } from "@/lib/benefit-showcase"
import { fetchActiveBenefits, fetchCustomerBenefits } from "@/lib/loyalty-benefits"
import type { ActiveBenefits, CustomerBenefits } from "@/types/loyalty-benefits"
import { useEffect, useState } from "react"
import { Badge, Callout, Card, Heading, Price, ProgressBar, SectionHeader, Text } from "@/components/ui"

function StoreBenefitCard({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <Card padding="lg">
      <Heading level={3} size="card" className="mb-1">{title}</Heading>
      <Text as="div" size="sm" tone="muted">{children}</Text>
    </Card>
  )
}

/**
 * Customer-facing view of the merchant's loyalty mechanics. Two parts:
 * store benefits (what the customer can earn) and the customer's own unlocked
 * entitlements + progress. Renders nothing when the merchant has no benefits.
 */
export function LoyaltyBenefitsSection({ merchantId }: { merchantId: string }) {
  const [active, setActive] = useState<ActiveBenefits | null>(null)
  const [customer, setCustomer] = useState<CustomerBenefits | null>(null)

  useEffect(() => {
    if (!merchantId) return
    fetchActiveBenefits(merchantId).then(setActive).catch(() => setActive(null))
    fetchCustomerBenefits(merchantId).then(setCustomer).catch(() => setCustomer(null))
  }, [merchantId])

  const cartThreshold = active?.cartThreshold ?? null
  const itemPromos = active?.itemPromos ?? []
  const activeRecurrence = active?.recurrence ?? null

  const entitlements = customer?.entitlements ?? []
  const recurrence = customer?.recurrenceProgress
  const itemProgress = customer?.itemSpendProgress ?? []

  const hasStoreBenefits = !!cartThreshold || itemPromos.length > 0 || !!activeRecurrence
  const hasCustomerBenefits =
    entitlements.length > 0 || (!!recurrence && !recurrence.exhausted) || itemProgress.length > 0

  if (!hasStoreBenefits && !hasCustomerBenefits) return null

  return (
    <div className="flex flex-col gap-6">
      {hasStoreBenefits && (
        <section className="flex flex-col gap-4">
          <SectionHeader title="Benefícios da loja" spacing="none" />

          {cartThreshold && (
            <StoreBenefitCard title="Compre mais e ganhe">
              Em compras a partir de{" "}
              <Price value={cartThreshold.minItemsSubtotal.value} weight="semibold" tone="default" />
              : {describeBenefit(cartThreshold.benefit)}.
            </StoreBenefitCard>
          )}

          {itemPromos.map((promo) => (
            <StoreBenefitCard
              key={promo.id}
              title={promo.triggerItem.name || "Promoção por item"}
            >
              <ul className="flex flex-col gap-1">
                {promo.tiers.map((tier) => (
                  <li key={tier.id}>
                    Gaste{" "}
                    <Price value={tier.minItemSpend.value} weight="semibold" tone="default" />{" "}
                    e ganhe {describeBenefit(tier.benefit)}.
                  </li>
                ))}
              </ul>
            </StoreBenefitCard>
          ))}

          {activeRecurrence && (
            <StoreBenefitCard title="Programa de recorrência">
              A cada{" "}
              <Text as="span" weight="semibold" tone="default">
                {activeRecurrence.requiredOrders} pedidos
              </Text>{" "}
              você ganha {activeRecurrence.reward.item.name || "um item"} grátis.
            </StoreBenefitCard>
          )}
        </section>
      )}

      {hasCustomerBenefits && (
        <section className="flex flex-col gap-4">
          <SectionHeader title="Seus benefícios" spacing="none" />

          {entitlements.map((g) => (
            <Callout
              key={g.grantId}
              tone="success"
              title={
                <span className="flex items-center justify-between gap-2">
                  {describeBenefit(g.benefit)}
                  <Badge tone="success" variant="solid">Desbloqueado</Badge>
                </span>
              }
            >
              Use no checkout ao finalizar o pedido.
            </Callout>
          ))}

          {recurrence && !recurrence.exhausted && (
            <Card padding="lg">
              <div className="mb-2 flex items-center justify-between">
                <Text as="span" weight="semibold" tone="default">
                  Programa de recorrência
                </Text>
                <Text as="span" size="sm" tone="muted">
                  {recurrence.ordersCounted} de {recurrence.requiredOrders} pedidos
                </Text>
              </div>
              <ProgressBar
                value={recurrence.ordersCounted / Math.max(recurrence.requiredOrders, 1)}
                size="lg"
                label="Progresso do programa de recorrência"
              />
            </Card>
          )}

          {itemProgress.map((p) => (
            <Card key={p.promoId} padding="xl">
              <div className="mb-1 flex items-center justify-between">
                <Text as="span" weight="semibold" tone="default">
                  {p.triggerItem.name || "Promoção por item"}
                </Text>
                <Text as="span" size="sm" tone="muted">
                  <Price value={p.accumulated.value} /> acumulados
                </Text>
              </div>
              {p.nextTierMinSpend && (
                <Text size="sm" tone="muted">
                  Faltam{" "}
                  <Price value={Math.max(p.nextTierMinSpend.value - p.accumulated.value, 0)} weight="semibold" />{" "}
                  para a próxima faixa.
                </Text>
              )}
            </Card>
          ))}
        </section>
      )}
    </div>
  )
}
